import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  OnInit,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material";
import { RewardInfoComponent } from "./reward-info/reward-info.component";
import { GameSettingsService } from "../game-settings.service";
import { ConfirmationDialogComponent } from "../../confirmation-dialog/confirmation-dialog.component";

export interface Reward {
  id?: string | number;
  type: "positive" | "negative" | "link";
  title: string;
  thumbnail: {
    image: string;
    isExpand?: boolean;
    scale?: number;
  };
  description?: string;
  url?: string;
  expirationTime?: string;
  surveyOnly?: boolean;
  isExpand?: boolean;
  background?: {
    image: string;
    scale: number;
    color1: string;
    color2: string;
  };
  counter?: {
    color1: string;
    color2: string;
  };
  coinIcon?: string;
  points?: number;
}

export interface ClubInvite {
  label: string;
  value: string;
  canAddReward: boolean;
  reward?: Reward; // only one reward
}

@Component({
  selector: "app-reward-selector",
  templateUrl: "./reward-selector.component.html",
  styleUrls: ["./reward-selector.component.scss"],
})
export class RewardSelectorComponent implements OnChanges, OnInit {
  // removed temporarly , "positive", "negative"
  types = ["none", "link"];

  @Input("clubInvites") clubInvites: ClubInvite[] = [];
  @Input("drawDate") drawDate: string = "";

  @Output() clubInvitesChange = new EventEmitter<ClubInvite[]>();

  confirmDialogRef: MatDialogRef<ConfirmationDialogComponent>;

  constructor(
    private dialog: MatDialog,
    public gameSettingsService: GameSettingsService
  ) {}

  ngOnInit() {
    this.updateAddRewardAvailability();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["clubInvites"]) {
      this.updateAddRewardAvailability();
    }
  }

  rewardTypeUsed(type: string): boolean {
    if (!type || type === "none") return false;
    const t = String(type).toLowerCase();
    for (var i = 0; i < this.clubInvites.length; i++) {
      if (
        this.clubInvites[i].reward &&
        String(this.clubInvites[i].reward.type).toLowerCase() === t
      ) {
        return true;
      }
    }
    return false;
  }

  rewardTypeUsedByOther(index: number, type: string): boolean {
    if (!type || type === "none") return false;
    const t = String(type).toLowerCase();
    for (let i = 0; i < this.clubInvites.length; i++) {
      if (i === index) continue;
      if (
        this.clubInvites[i].reward &&
        String(this.clubInvites[i].reward.type).toLowerCase() === t
      ) {
        return true;
      }
    }
    return false;
  }

  addReward(index: number) {
    var self = this;
    var dialogRef = this.dialog.open(RewardInfoComponent, {
      width: "600px",
      panelClass: "custom-count-dialog",
      data: {
        type: this.clubInvites[index].value,
        reward: this.clubInvites[index].reward,
        drawDate: this.drawDate,
      },
    });

    dialogRef.afterClosed().subscribe(function (result: Reward | null) {
      if (result) {
        self.clubInvites[index].reward = result;
        self.clubInvitesChange.emit(self.clubInvites);
        self.updateAddRewardAvailability();
      }
    });
  }

  removeReward(index: number) {
    this.confirmDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: "Are you sure you want to remove this reward?",
      },
    });

    this.confirmDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Remove the reward at the current index and shift all following rewards up
        for (let i = index; i < this.clubInvites.length - 1; i++) {
          this.clubInvites[i].reward = this.clubInvites[i + 1].reward;
          this.clubInvites[i].value = this.clubInvites[i + 1].value;
          this.clubInvites[i].canAddReward = this.clubInvites[i + 1].canAddReward;
        }

        // Clear the last slot
        const last = this.clubInvites[this.clubInvites.length - 1];
        last.reward = undefined;
        last.value = "none";
        last.canAddReward = false;

        // Emit the updated array
        this.clubInvitesChange.emit(this.clubInvites);

        // Recalculate availability
        this.updateAddRewardAvailability();
      }
    });
  }

  onTypeChange(index: number) {
    const selector = this.clubInvites[index];
  
    if (selector.value === "none") {
      // Remove the reward at the current index
      selector.reward = undefined;
      selector.canAddReward = false;
  
      // Shift all following rewards up
      for (let i = index; i < this.clubInvites.length - 1; i++) {
        this.clubInvites[i].reward = this.clubInvites[i + 1].reward;
        this.clubInvites[i].value = this.clubInvites[i + 1].value;
        this.clubInvites[i].canAddReward = this.clubInvites[i + 1].canAddReward;
      }
  
      // Clear the last slot
      const last = this.clubInvites[this.clubInvites.length - 1];
      last.reward = undefined;
      last.value = "none";
      last.canAddReward = false;
  
      // Emit the updated array
      this.clubInvitesChange.emit(this.clubInvites);
  
      // Recalculate availability
      this.updateAddRewardAvailability();
    } else {
      this.updateAddRewardAvailability();
    }
  }

  updateAddRewardAvailability() {
    for (let i = 0; i < this.clubInvites.length; i++) {
      const selector = this.clubInvites[i];
      const hasType = !!selector.value && selector.value !== "none";
      const typeAvailable = hasType;
      const prevReady = i === 0 ? true : !!this.clubInvites[i - 1].reward;
      selector.canAddReward = !!typeAvailable && !!prevReady;
    }
  }
}
